import { DeployEnv } from '@/env/type';
import escapeRegExp from 'lodash/escapeRegExp';
import { isApiRequestError, API_REQUEST_ERROR_NAME } from '@/request/error';
import { isRequestCancellation } from '@/request/transport-error';

const AUTH0_TOKEN_PATH = '/api/auth/auth0token';
const UNKNOWN_API_CONTRACT = 'unknown_contract';
const API_LOG_TYPE = 'api';
const CUSTOM_ERROR_LEVEL = 'custom_error';
const ASSET_SPEED_LOG_TYPE = 'assets_speed';
const ASSET_SPEED_MESSAGE = 'asset_speed';
const CRITICAL_ASSET_TYPES = new Set(['js', 'css']);
const API_DETAIL_PAYLOAD_FIELDS = ['data', 'param', 'reqHeaders', 'resHeaders', 'urlQuery'];
const API_TRANSPORT_ERROR_PREFIXES = [
  'AJAX_ERROR: request timeout', 'AJAX_ERROR: request error',
  'AJAX_ERROR: request abort', 'AJAX_ERROR: request failed', 'FETCH_ERROR',
];
const CUSTOM_ERROR_FIELDS = [
  'msg', 'errorMsg', 'level', 'type', 'plugin', 'timestamp', 'aegisv2_goto',
  'isWhiteListDyeLog', 'snapshootInfo',
];

export const MONITORING_ENDPOINT_PATTERNS = [
  /tgalileo\.com\/collect/,
  /galileotelemetry\.tencent\.com\/collect/,
];

export interface ApiRetCodeResult {
  isErr: boolean;
  code: string | number;
}

export interface ApiResponseSource {
  readonly status: number;
  readonly responseURL?: string;
  readonly url?: string;
}

export interface TraceApiConfig {
  injectTraceHeader: 'traceparent';
  injectTraceUrls: RegExp[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseResponseData(data: unknown): Record<string, unknown> | undefined {
  if (isRecord(data)) return data;
  if (typeof data !== 'string') return undefined;

  try {
    const parsed: unknown = JSON.parse(data);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function normalizeResponseCode(code: unknown): string | number | undefined {
  return typeof code === 'string' || typeof code === 'number' ? code : undefined;
}

function isSuccessCode(code: string | number): boolean {
  return code === 0 || code === '0';
}

function parseBusinessResult(data: unknown, responseUrl: string): ApiRetCodeResult {
  const payload = parseResponseData(data);
  if (!payload) return { isErr: true, code: UNKNOWN_API_CONTRACT };

  if (responseUrl.includes(AUTH0_TOKEN_PATH) && typeof payload.token === 'string' && payload.token) {
    return { isErr: false, code: '0' };
  }

  // TODO(backend-error-contract, TAPD-138365185): 后端统一错误协议后删除双协议兼容。
  const commonResult = isRecord(payload.common_result) ? payload.common_result : undefined;
  const commonResultCode = normalizeResponseCode(commonResult?.code);
  if (commonResultCode !== undefined) {
    return { isErr: !isSuccessCode(commonResultCode), code: commonResultCode };
  }

  if (typeof payload.status === 'string') {
    const isSuccess = payload.status === '' || payload.status === 'success' || payload.status === 'info';
    const errorCode = normalizeResponseCode(payload.code) ?? payload.status;
    return { isErr: !isSuccess, code: isSuccess ? '0' : errorCode };
  }

  const code = normalizeResponseCode(payload.code);
  if (code !== undefined) {
    return { isErr: !isSuccessCode(code), code };
  }

  return { isErr: true, code: UNKNOWN_API_CONTRACT };
}

function containsMonitoringEndpoint(value: unknown): boolean {
  return typeof value === 'string'
    && MONITORING_ENDPOINT_PATTERNS.some(pattern => pattern.test(value));
}

function isFailedHttpStatus(value: unknown): boolean {
  return typeof value === 'number' && value >= 400 && value < 600;
}

function normalizeAssetSpeedLog(log: unknown): unknown {
  if (!isRecord(log) || (log.type !== ASSET_SPEED_LOG_TYPE && log.msg !== ASSET_SPEED_MESSAGE)) return log;
  if (typeof log.assetType === 'string' && log.assetType !== '' && log.assetType !== 'static') return log;
  if (typeof log.url !== 'string') return log;

  // SDK 缺少 Resource Timing 时类型为 static，只按路径后缀补全，避免 query 中的扩展名误判。
  try {
    const url = new URL(log.url, location.origin);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return log;
    const extension = /\.(js|mjs|cjs|css)$/i.exec(url.pathname);
    return extension ? { ...log, assetType: extension[1].toLowerCase() === 'css' ? 'css' : 'js' } : log;
  } catch {
    return log;
  }
}

function shouldReportAssetSpeedLog(log: Record<string, unknown>): boolean {
  const isAssetSpeedLog = log.type === ASSET_SPEED_LOG_TYPE || log.msg === ASSET_SPEED_MESSAGE;
  if (!isAssetSpeedLog) return true;

  if (typeof log.assetType !== 'string' || !CRITICAL_ASSET_TYPES.has(log.assetType)) return false;

  // 跨域或旧浏览器的 responseStatus 可能不可见，仅凭 0/缺失不能判定加载失败。
  return log.msg === 'static_fail'
    || (typeof log.errorType === 'string' && log.errorType.length > 0)
    || isFailedHttpStatus(log.status)
    || isFailedHttpStatus(log.assetResponseStatus);
}

function shouldReportLog(log: unknown): boolean {
  if (!isRecord(log)) return true;
  if (!shouldReportAssetSpeedLog(log)) return false;

  const message = typeof log.msg === 'string' ? log.msg : '';
  const originEvent = isRecord(log.originEvent) ? log.originEvent : undefined;
  if (log.type !== API_LOG_TYPE && (
    isApiRequestError(log)
    || isApiRequestError(originEvent?.reason)
    || isApiRequestError(originEvent?.error)
    || isRequestCancellation(originEvent?.reason)
    || isRequestCancellation(originEvent?.error)
  )) return false;
  if (log.type !== API_LOG_TYPE && message.includes(API_REQUEST_ERROR_NAME)) return false;
  if (containsMonitoringEndpoint(message) || containsMonitoringEndpoint(log.url)) return false;

  return true;
}

function sanitizeApiUrl(value: unknown): string {
  if (typeof value !== 'string' || !value) return 'unknown';

  try {
    const url = new URL(value, location.origin);
    return url.protocol === 'http:' || url.protocol === 'https:' ? `${url.origin}${url.pathname}` : 'unknown';
  } catch {
    return 'unknown';
  }
}

function sanitizeApiLog(log: unknown): unknown {
  if (!isRecord(log) || log.type !== API_LOG_TYPE) return log;

  const sanitized = { ...log };
  // SDK 的 msg/errorMsg 也可能含完整 URL，重建摘要以阻断 query 中的凭证。
  const url = sanitizeApiUrl(log.url);
  const status = typeof log.status === 'number' ? log.status : 'unknown';
  const code = normalizeResponseCode(log.ret) ?? 'unknown';
  const transportError = API_TRANSPORT_ERROR_PREFIXES.find(prefix => typeof log.msg === 'string' && log.msg.startsWith(prefix));
  sanitized.url = url;
  sanitized.msg = [transportError, `url: ${url}`, `status: ${status}`, `retcode: ${code}`].filter(Boolean).join('\n');
  if (log.errorMsg !== undefined) sanitized.errorMsg = `${url},${code}`;
  API_DETAIL_PAYLOAD_FIELDS.forEach(field => delete sanitized[field]);
  return sanitized;
}

function sanitizeCustomErrorLog(log: unknown): unknown {
  if (!isRecord(log) || log.level !== CUSTOM_ERROR_LEVEL) return log;
  // 自定义异常仅保留 SDK 解析 Issue 和 Sourcemap 所需字段，阻断额外对象属性。
  return Object.fromEntries(Object.entries(log).filter(([field]) => CUSTOM_ERROR_FIELDS.includes(field)));
}

export function getAegisEnv(deployEnv: DeployEnv): 'production' | 'pre' | 'test' | null {
  if (deployEnv === DeployEnv.LOCAL) return null;
  if (deployEnv === DeployEnv.PROD) return 'production';
  if (deployEnv === DeployEnv.PRE) return 'pre';
  return 'test';
}

function normalizeApiResponse(source: ApiResponseSource): { status: number; url: string } {
  return {
    status: source.status,
    url: source.responseURL || source.url || '',
  };
}

export function handleApiResponse(
  data: unknown,
  requestUrl: string,
  source: ApiResponseSource,
): ApiRetCodeResult {
  const response = normalizeApiResponse(source);
  const isHttpError = !(response.status >= 200 && response.status < 300);
  const isAbcApi = /api[.-]/.test(requestUrl) && /abetterchoice[.-]/.test(requestUrl);

  if (!isAbcApi) {
    return { isErr: isHttpError, code: String(response.status) };
  }

  if (isHttpError) {
    return { isErr: true, code: response.status };
  }

  return parseBusinessResult(data, response.url);
}

export function filterGalileoLogs(logs: unknown[]): unknown[] {
  return logs.map(normalizeAssetSpeedLog)
    .filter(shouldReportLog)
    .map(sanitizeApiLog)
    .map(sanitizeCustomErrorLog);
}

export function isGalileoTraceEnabled(value: string | boolean | undefined): boolean {
  return value === 'true' || value === true;
}

export function buildTraceApiConfig(enabled: boolean, apiBaseUrl: string): TraceApiConfig | Record<string, never> {
  if (!enabled) return {};

  // TODO(backend-trace, TAPD-138365185): 网关完成 traceparent 透传验证后再默认开启。
  return {
    injectTraceHeader: 'traceparent',
    injectTraceUrls: [new RegExp(`^${escapeRegExp(apiBaseUrl)}`)],
  };
}
